import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {CbEntityCoverage, CbModuleCoverage} from "../../model/canvas/cb-coverage-node.interface";

/**
 * Node representation for canvas rendering.
 */
interface CanvasNode {
    id: string;
    type: 'module' | 'entity';
    label: string;
    sublabel?: string;
    coverage: number;
    documented: number;
    total: number;
    x: number;
    y: number;
    width: number;
    height: number;
    expanded: boolean;
    selected: boolean;
    hovered: boolean;
    data: CbModuleCoverage | CbEntityCoverage;
    children?: CanvasNode[];
    parentId?: string;
}

/**
 * Connection line between nodes.
 */
interface CanvasConnection {
    sourceId: string;
    targetId: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

/**
 * CB Schema Coverage Canvas Component.
 * Visualizes documentation coverage across modules and entities.
 */
@Component({
    selector: 'cb-schema-coverage-canvas',
    templateUrl: './cb-schema-coverage-canvas.component.html',
    styleUrls: ['./cb-schema-coverage-canvas.component.scss'],
    standalone: false
})
export class CbSchemaCoverageCanvasComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    @Input() coverageData: CbModuleCoverage[] = [];
    @Input() loading: boolean = false;

    @Output() entitySelected = new EventEmitter<CbEntityCoverage>();

    @ViewChild('canvasContainer') canvasContainer: ElementRef<HTMLDivElement>;
    @ViewChild('canvas') canvas: ElementRef<SVGElement>;

    // Canvas state
    nodes: CanvasNode[] = [];
    connections: CanvasConnection[] = [];

    // Viewport
    panX: number = 0;
    panY: number = 0;
    zoom: number = 1;
    minZoom: number = 0.25;
    maxZoom: number = 2;

    // Canvas dimensions
    canvasWidth: number = 3000;
    canvasHeight: number = 2000;

    // Node dimensions
    moduleNodeWidth: number = 180;
    moduleNodeHeight: number = 80;
    entityNodeWidth: number = 160;
    entityNodeHeight: number = 60;

    // Layout
    moduleSpacingX: number = 250;
    moduleSpacingY: number = 200;
    entitySpacingX: number = 180;
    entitySpacingY: number = 80;
    modulesPerRow: number = 4;

    // Interaction state
    isPanning: boolean = false;
    panStartX: number = 0;
    panStartY: number = 0;
    selectedNodeId: string | null = null;
    hoveredNodeId: string | null = null;

    // Filter
    showDocumentedOnly: boolean = false;
    showUndocumentedOnly: boolean = false;
    searchTerm: string = '';

    constructor() {
    }

    ngOnInit(): void {
    }

    ngAfterViewInit(): void {
        this.centerCanvas();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['coverageData'] && this.coverageData) {
            this.buildNodes();
            this.calculateLayout();
            this.buildConnections();
        }
    }

    ngOnDestroy(): void {
    }

    /**
     * Build nodes from coverage data.
     */
    private buildNodes(): void {
        this.nodes = [];

        this.coverageData.forEach((module, index) => {
            const moduleNode: CanvasNode = {
                id: `module-${module.moduleName}`,
                type: 'module',
                label: module.moduleLabel,
                sublabel: module.moduleName.toUpperCase(),
                coverage: module.coveragePercent,
                documented: module.documentedEntities,
                total: module.totalEntities,
                x: 0,
                y: 0,
                width: this.moduleNodeWidth,
                height: this.moduleNodeHeight,
                expanded: false,
                selected: false,
                hovered: false,
                data: module,
                children: []
            };

            // Create entity child nodes
            module.entities.forEach(entity => {
                const entityNode: CanvasNode = {
                    id: `entity-${entity.objectTypeAlias}`,
                    type: 'entity',
                    label: entity.entityName,
                    sublabel: `${entity.documentedAttributes}/${entity.totalAttributes} attrs`,
                    coverage: entity.coveragePercent,
                    documented: entity.documentedAttributes,
                    total: entity.totalAttributes,
                    x: 0,
                    y: 0,
                    width: this.entityNodeWidth,
                    height: this.entityNodeHeight,
                    expanded: false,
                    selected: false,
                    hovered: false,
                    data: entity,
                    parentId: moduleNode.id
                };

                moduleNode.children.push(entityNode);
            });

            this.nodes.push(moduleNode);
        });
    }

    /**
     * Calculate node positions.
     */
    private calculateLayout(): void {
        let currentX = 100;
        let currentY = 100;
        let rowIndex = 0;

        this.nodes.forEach((node, index) => {
            // Position module node
            node.x = currentX;
            node.y = currentY;

            // Position entity children if expanded
            if (node.expanded && node.children) {
                let entityY = node.y + node.height + 30;

                node.children.forEach((child, childIndex) => {
                    child.x = node.x + 20;
                    child.y = entityY;
                    entityY += child.height + 15;
                });
            }

            // Move to next column
            currentX += this.moduleSpacingX;

            // Check if we need to start a new row
            if ((index + 1) % this.modulesPerRow === 0) {
                currentX = 100;
                currentY += this.getModuleRowHeight(rowIndex);
                rowIndex++;
            }
        });
    }

    /**
     * Get the height needed for a row of modules.
     */
    private getModuleRowHeight(rowIndex: number): number {
        const startIndex = rowIndex * this.modulesPerRow;
        const endIndex = Math.min(startIndex + this.modulesPerRow, this.nodes.length);
        let maxHeight = this.moduleNodeHeight + this.moduleSpacingY;

        for (let i = startIndex; i < endIndex; i++) {
            const node = this.nodes[i];
            if (node.expanded && node.children) {
                const expandedHeight = node.height + 30 + (node.children.length * (this.entityNodeHeight + 15));
                maxHeight = Math.max(maxHeight, expandedHeight + 50);
            }
        }

        return maxHeight;
    }

    /**
     * Build connections between nodes.
     */
    private buildConnections(): void {
        this.connections = [];

        this.nodes.forEach(moduleNode => {
            if (moduleNode.expanded && moduleNode.children) {
                moduleNode.children.forEach(entityNode => {
                    this.connections.push({
                        sourceId: moduleNode.id,
                        targetId: entityNode.id,
                        x1: moduleNode.x + moduleNode.width / 2,
                        y1: moduleNode.y + moduleNode.height,
                        x2: entityNode.x,
                        y2: entityNode.y + entityNode.height / 2
                    });
                });
            }
        });
    }

    /**
     * Center canvas in viewport.
     */
    centerCanvas(): void {
        if (!this.canvasContainer) return;

        const container = this.canvasContainer.nativeElement;
        this.panX = (container.clientWidth - this.canvasWidth * this.zoom) / 2;
        this.panY = 50;
    }

    /**
     * Zoom in.
     */
    zoomIn(): void {
        this.setZoom(this.zoom * 1.2);
    }

    /**
     * Zoom out.
     */
    zoomOut(): void {
        this.setZoom(this.zoom / 1.2);
    }

    /**
     * Reset zoom to 100%.
     */
    resetZoom(): void {
        this.setZoom(1);
        this.centerCanvas();
    }

    /**
     * Fit all nodes in viewport.
     */
    fitToViewport(): void {
        if (!this.canvasContainer) return;

        const container = this.canvasContainer.nativeElement;
        const bounds = this.getNodesBounds();

        const scaleX = container.clientWidth / (bounds.maxX - bounds.minX + 200);
        const scaleY = container.clientHeight / (bounds.maxY - bounds.minY + 200);
        const scale = Math.min(scaleX, scaleY, 1);

        this.setZoom(scale);
        this.panX = (container.clientWidth - (bounds.maxX - bounds.minX) * scale) / 2 - bounds.minX * scale;
        this.panY = (container.clientHeight - (bounds.maxY - bounds.minY) * scale) / 2 - bounds.minY * scale;
    }

    /**
     * Set zoom level.
     */
    private setZoom(zoom: number): void {
        this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, zoom));
    }

    /**
     * Get bounds of all nodes.
     */
    private getNodesBounds(): { minX: number; minY: number; maxX: number; maxY: number } {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        const processNode = (node: CanvasNode) => {
            minX = Math.min(minX, node.x);
            minY = Math.min(minY, node.y);
            maxX = Math.max(maxX, node.x + node.width);
            maxY = Math.max(maxY, node.y + node.height);

            if (node.expanded && node.children) {
                node.children.forEach(processNode);
            }
        };

        this.nodes.forEach(processNode);

        return { minX, minY, maxX, maxY };
    }

    /**
     * Handle mouse wheel for zooming.
     */
    @HostListener('wheel', ['$event'])
    onWheel(event: WheelEvent): void {
        if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            const delta = event.deltaY > 0 ? 0.9 : 1.1;
            this.setZoom(this.zoom * delta);
        }
    }

    /**
     * Handle mouse down for panning.
     */
    onCanvasMouseDown(event: MouseEvent): void {
        if (event.button === 0 && !this.isClickOnNode(event)) {
            this.isPanning = true;
            this.panStartX = event.clientX - this.panX;
            this.panStartY = event.clientY - this.panY;
            event.preventDefault();
        }
    }

    /**
     * Handle mouse move for panning.
     */
    onCanvasMouseMove(event: MouseEvent): void {
        if (this.isPanning) {
            this.panX = event.clientX - this.panStartX;
            this.panY = event.clientY - this.panStartY;
        }
    }

    /**
     * Handle mouse up to stop panning.
     */
    onCanvasMouseUp(event: MouseEvent): void {
        this.isPanning = false;
    }

    /**
     * Check if click is on a node.
     */
    private isClickOnNode(event: MouseEvent): boolean {
        const target = event.target as HTMLElement;
        return target.closest('.canvas-node') !== null;
    }

    /**
     * Handle node click.
     */
    onNodeClick(node: CanvasNode, event: MouseEvent): void {
        event.stopPropagation();

        if (node.type === 'module') {
            // Toggle expansion
            node.expanded = !node.expanded;
            this.calculateLayout();
            this.buildConnections();
        } else if (node.type === 'entity') {
            // Select entity for editing
            this.selectedNodeId = node.id;
            this.nodes.forEach(n => {
                n.selected = n.id === node.id;
                if (n.children) {
                    n.children.forEach(c => c.selected = c.id === node.id);
                }
            });
            this.entitySelected.emit(node.data as CbEntityCoverage);
        }
    }

    /**
     * Handle node double click.
     */
    onNodeDoubleClick(node: CanvasNode, event: MouseEvent): void {
        event.stopPropagation();

        if (node.type === 'entity') {
            this.entitySelected.emit(node.data as CbEntityCoverage);
        }
    }

    /**
     * Handle node mouse enter.
     */
    onNodeMouseEnter(node: CanvasNode): void {
        this.hoveredNodeId = node.id;
        node.hovered = true;
    }

    /**
     * Handle node mouse leave.
     */
    onNodeMouseLeave(node: CanvasNode): void {
        this.hoveredNodeId = null;
        node.hovered = false;
    }

    /**
     * Get coverage color based on percentage.
     */
    getCoverageColor(percent: number): string {
        if (percent >= 80) return 'var(--green-500)';
        if (percent >= 50) return 'var(--orange-500)';
        return 'var(--red-500)';
    }

    /**
     * Get coverage background color.
     */
    getCoverageBgColor(percent: number): string {
        if (percent >= 80) return 'var(--green-100)';
        if (percent >= 50) return 'var(--orange-100)';
        return 'var(--red-100)';
    }

    /**
     * Get progress bar width.
     */
    getProgressWidth(percent: number): number {
        return Math.min(100, Math.max(0, percent));
    }

    /**
     * Get documentation status icon.
     */
    getStatusIcon(entity: CbEntityCoverage): string {
        if (entity.coveragePercent === 100) return 'fa-solid fa-check-circle';
        if (entity.isDocumented) return 'fa-solid fa-circle-half-stroke';
        return 'fa-regular fa-circle';
    }

    /**
     * Get status icon color.
     */
    getStatusColor(entity: CbEntityCoverage): string {
        if (entity.coveragePercent === 100) return 'var(--green-500)';
        if (entity.isDocumented) return 'var(--orange-500)';
        return 'var(--red-500)';
    }

    /**
     * Get visible nodes (for filtering).
     */
    getVisibleNodes(): CanvasNode[] {
        return this.nodes.filter(node => this.isNodeVisible(node));
    }

    /**
     * Check if node should be visible based on filters.
     */
    private isNodeVisible(node: CanvasNode): boolean {
        if (this.searchTerm) {
            const term = this.searchTerm.toLowerCase();
            if (!node.label.toLowerCase().includes(term)) {
                return false;
            }
        }
        return true;
    }

    /**
     * Expand all modules.
     */
    expandAll(): void {
        this.nodes.forEach(node => node.expanded = true);
        this.calculateLayout();
        this.buildConnections();
    }

    /**
     * Collapse all modules.
     */
    collapseAll(): void {
        this.nodes.forEach(node => node.expanded = false);
        this.calculateLayout();
        this.buildConnections();
    }

    /**
     * Get all entity nodes (flattened).
     */
    getAllEntityNodes(): CanvasNode[] {
        const entities: CanvasNode[] = [];
        this.nodes.forEach(module => {
            if (module.children) {
                entities.push(...module.children);
            }
        });
        return entities;
    }

    /**
     * Get transform style for canvas.
     */
    getCanvasTransform(): string {
        return `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
    }
}
